import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarClock, MessageSquare, FileText, Sparkles, ArrowUpRight,
  MapPin, Users, Mic, Target, FolderOpen, ClipboardList, User as UserIcon,
  Clock, ChevronRight, Activity, BadgeCheck, FileSignature,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { HealthSnapshotWidget } from "@/components/player/HealthSnapshotWidget";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Phase 2 — branded Parent Portal hub.
//
// Distinct from the player Dashboard: this is a concierge-style "today" view
// for families — what's next on the schedule, what's just been shared by the
// advisor, the latest approved report, and the open conversations. Surfaces
// only data the family is allowed to see (server-enforced).
// ---------------------------------------------------------------------------

const EVENT_TYPE_LABEL = {
  meeting: "Meeting",
  family_call: "Family Call",
  visit: "Team Visit",
  combine: "Combine",
  pro_day: "Pro Day",
  interview: "Interview",
  training: "Training",
  media: "Media",
  personal: "Personal",
  other: "Event",
};

const AGENT_LABEL = {
  scouting_report: "Scouting Report",
  draft_strategy: "Draft Strategy",
  team_fit: "Team Fit",
  player_360: "Player 360°",
  report_draft: "Report",
  interview_summary: "Interview Summary",
  interview_evaluator: "Interview Score",
  player_narrative: "Player Narrative",
  weekly_progress: "Weekly Progress",
};

function fmtDateLong(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const tileSpring = { duration: 0.5, ease: [0.16, 1, 0.3, 1] };

function Tile({ children, className = "", delay = 0, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...tileSpring, delay }}
      className={className}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}

export default function FamilyPortalPage() {
  useDocumentTitle("OmniDraftLab · Family Hub");
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ player: null, events: [], reports: [], threads: [], activity: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Identify the primary linked athlete. Family role: server gives back
        // user.player_id for the legacy single-link case (also covers seed).
        const playerId = user?.player_id;
        const [evRes, threadsRes, activityRes, decisionsRes, drillsRes] = await Promise.allSettled([
          api.get("/events/agenda/upcoming", { params: { days: 30 } }),
          api.get("/threads"),
          api.get("/agent-outputs", { params: { limit: 8, status: "published,approved" } }),
          playerId ? api.get("/decisions", { params: { player_id: playerId } }) : Promise.resolve({ data: [] }),
          playerId ? api.get("/prep-drills", { params: { player_id: playerId } }) : Promise.resolve({ data: [] }),
        ]);

        let player = null;
        let reports = [];
        if (playerId) {
          const [pRes, rRes] = await Promise.allSettled([
            api.get(`/players/${playerId}`),
            api.get("/reports", { params: { player_id: playerId } }),
          ]);
          if (pRes.status === "fulfilled") player = pRes.value.data;
          if (rRes.status === "fulfilled") reports = rRes.value.data || [];
        }

        // Build a unified activity feed across surfaces (newest first) so the
        // family sees everything the advisory team is doing — not just agent
        // outputs. Each entry carries a `kind` so the renderer can pick its
        // icon, color, and click target.
        const feed = [];
        const addItems = (xs, builder) => (xs || []).forEach((x) => {
          const e = builder(x);
          if (e) feed.push(e);
        });
        if (activityRes.status === "fulfilled") {
          addItems(activityRes.value.data?.items, (a) => ({
            kind: "agent_output",
            id: a.id,
            title: a.summary || a.agent_key,
            subtitle: AGENT_LABEL[a.agent_key] || a.agent_key,
            status: a.status,
            ts: a.published_at || a.approved_at || a.created_at,
            href: "/app/me/reports",
          }));
        }
        if (decisionsRes.status === "fulfilled") {
          addItems(decisionsRes.value.data, (d) => ({
            kind: "decision",
            id: d.id,
            title: d.title,
            subtitle: d.chosen_option ? `Chosen · ${d.chosen_option}` : "Decision",
            status: d.status,
            ts: d.updated_at || d.created_at,
            href: "/app/me/decisions",
          }));
        }
        if (drillsRes.status === "fulfilled") {
          addItems(drillsRes.value.data, (x) => ({
            kind: "drill",
            id: x.id,
            title: x.title,
            subtitle: `${x.drill_type} drill`,
            status: x.status,
            ts: x.completed_at || x.updated_at || x.created_at,
            href: "/app/me/prep",
          }));
        }
        feed.sort((a, b) => String(b.ts || "").localeCompare(String(a.ts || "")));

        if (cancelled) return;
        setData({
          player,
          events: evRes.status === "fulfilled" ? (evRes.value.data || []) : [],
          threads: threadsRes.status === "fulfilled" ? (threadsRes.value.data || []) : [],
          activity: feed.slice(0, 10),
          reports,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.player_id]);

  const approvedReports = useMemo(
    () => (data.reports || [])
      .filter((r) => r.status === "approved")
      .sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || ""))),
    [data.reports]
  );

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="family-portal-page">
      {/* Hero */}
      <Tile testId="family-hero">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent p-6 lg:p-8">
          <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.18), transparent 70%)" }} />
          <div className="flex items-center gap-2 text-3 text-xs uppercase tracking-[0.2em]">
            <BadgeCheck className="h-3.5 w-3.5 text-cyan-300" /> Family Hub
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-1">
            Hello, <span className="text-cyan-300">{user?.full_name?.split(" ")[0] || "family"}</span>.
          </h1>
          <p className="mt-2 text-2 text-base max-w-2xl">
            {data.player
              ? <>Today's snapshot for <span className="text-1 font-semibold">{data.player.full_name}</span> — {data.player.position}{data.player.school ? `, ${data.player.school}` : ""}.</>
              : "Your athlete's schedule, reports, and conversations — at a glance."}
          </p>
          {data.player && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <StatusPill tone="cyan">{data.player.projected_round || "Unprojected"}</StatusPill>
              {data.player.projected_pick_range && <StatusPill tone="ice">{data.player.projected_pick_range}</StatusPill>}
              <StatusPill tone="neutral">{data.player.archetype || data.player.position}</StatusPill>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="family-cta-profile" onClick={() => nav("/app/me/profile")} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-xl">
              Open Player Profile <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
            <Button data-testid="family-cta-calendar" onClick={() => nav("/app/calendar")} variant="ghost" className="text-2 hover:text-white">
              View Calendar
            </Button>
          </div>
        </div>
      </Tile>

      {/* Bento grid */}
      <div className="mt-6 grid grid-cols-12 gap-4 md:gap-5">
        <ScheduleTile events={data.events} nav={nav} />
        <LatestReportTile reports={approvedReports} nav={nav} />
        {data.player && (
          <Tile className="col-span-12 lg:col-span-6" delay={0.16} testId="family-health-tile">
            <HealthSnapshotWidget
              player={data.player}
              isAdmin={false}
              testId="family-health-snapshot"
            />
          </Tile>
        )}
        <MessagesTile threads={data.threads} nav={nav} userId={user?.id} />
        <ActivityTile items={data.activity} nav={nav} />
        <QuickLinksStrip nav={nav} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule — next ~5 upcoming events
// ---------------------------------------------------------------------------
function ScheduleTile({ events, nav }) {
  const next = (events || []).slice(0, 5);
  return (
    <Tile className="col-span-12 lg:col-span-6" delay={0.06} testId="family-schedule-tile">
      <GlassCard className="p-6 h-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">Up Next</h3>
          </div>
          <button data-testid="family-schedule-all" onClick={() => nav("/app/calendar")} className="text-cyan-300 text-xs flex items-center gap-1 hover:text-cyan-200">
            Full calendar <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {next.length === 0 ? (
          <div className="mt-6 glass p-4 text-2 text-sm">
            Nothing scheduled in the next 30 days. Your advisor will share new dates here.
          </div>
        ) : (
          <ul className="mt-4 space-y-2">
            {next.map((e, i) => (
              <li key={e.id} data-testid={`family-event-${i}`} className="glass p-3 flex items-start gap-3 hover:ring-1 hover:ring-cyan-300/30 transition">
                <div className="h-12 w-12 rounded-lg bg-cyan-300/10 ring-1 ring-cyan-300/25 flex flex-col items-center justify-center shrink-0">
                  <div className="text-[9px] uppercase tracking-wider text-cyan-200/80 font-mono leading-none">
                    {new Date(e.start_at).toLocaleDateString(undefined, { month: "short" })}
                  </div>
                  <div className="mt-1 text-1 text-sm font-semibold font-mono leading-none">
                    {new Date(e.start_at).getDate()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-1 text-sm font-medium truncate">{e.title}</div>
                    <StatusPill tone="cyan">{EVENT_TYPE_LABEL[e.event_type] || "Event"}</StatusPill>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-3 text-xs">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDateLong(e.start_at)} · {fmtTime(e.start_at)}</span>
                    {e.location && <span className="inline-flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{e.location}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </Tile>
  );
}

// ---------------------------------------------------------------------------
// Latest approved Report
// ---------------------------------------------------------------------------
function LatestReportTile({ reports, nav }) {
  const latest = reports[0];
  const others = reports.slice(1, 4);
  return (
    <Tile className="col-span-12 lg:col-span-6" delay={0.1} testId="family-report-tile">
      <GlassCard className="p-6 h-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">Latest Report</h3>
          </div>
          <button data-testid="family-reports-all" onClick={() => nav("/app/me/reports")} className="text-cyan-300 text-xs flex items-center gap-1 hover:text-cyan-200">
            All reports <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {!latest ? (
          <div className="mt-6 glass p-4 text-2 text-sm">
            No approved reports yet. Once your advisor approves a report it'll surface here for you to read & download.
          </div>
        ) : (
          <>
            <button
              data-testid="family-report-latest"
              onClick={() => nav("/app/me/reports")}
              className="mt-4 w-full text-left glass p-4 hover:ring-1 hover:ring-cyan-300/30 transition"
            >
              <div className="flex items-center justify-between">
                <StatusPill tone="emerald">Approved</StatusPill>
                <span className="text-3 text-xs font-mono">{fmtRelative(latest.updated_at || latest.created_at)}</span>
              </div>
              <div className="mt-2 text-1 text-base font-semibold tracking-tight">{latest.title}</div>
              <div className="mt-1 text-3 text-xs uppercase tracking-wider">{latest.type?.replace(/_/g, " ")}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-cyan-300 text-xs">
                Open report <ArrowUpRight className="h-3 w-3" />
              </div>
            </button>

            {others.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {others.map((r, i) => (
                  <li key={r.id} data-testid={`family-report-recent-${i}`} className="flex items-center gap-3 text-sm py-2 px-1">
                    <div className="h-2 w-2 rounded-full bg-emerald-300 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-1 truncate">{r.title}</div>
                      <div className="text-3 text-[11px]">{r.type?.replace(/_/g, " ")} · {fmtRelative(r.updated_at || r.created_at)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </GlassCard>
    </Tile>
  );
}

// ---------------------------------------------------------------------------
// Messages — recent threads
// ---------------------------------------------------------------------------
function MessagesTile({ threads, nav, userId }) {
  const recent = (threads || []).slice(0, 4);
  return (
    <Tile className="col-span-12 lg:col-span-6" delay={0.14} testId="family-messages-tile">
      <GlassCard className="p-6 h-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">Conversations</h3>
          </div>
          <button data-testid="family-messages-all" onClick={() => nav("/app/messaging")} className="text-cyan-300 text-xs flex items-center gap-1 hover:text-cyan-200">
            Open inbox <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {recent.length === 0 ? (
          <div className="mt-6 glass p-4 text-2 text-sm">
            No active threads yet. Your advisor will start a conversation here.
          </div>
        ) : (
          <ul className="mt-4 space-y-2">
            {recent.map((t, i) => (
              <li key={t.id} data-testid={`family-thread-${i}`}>
                <button
                  onClick={() => nav("/app/messaging")}
                  className="w-full text-left glass p-3 hover:ring-1 hover:ring-cyan-300/30 transition flex items-start gap-3"
                >
                  <div className="h-9 w-9 rounded-full bg-white/5 ring-1 ring-white/10 grid place-items-center shrink-0">
                    <Users className="h-4 w-4 text-cyan-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-1 text-sm font-medium truncate">{t.title || "Conversation"}</div>
                      <span className="text-3 text-[11px] font-mono shrink-0">{fmtRelative(t.last_message_at || t.created_at)}</span>
                    </div>
                    <div className="text-2 text-xs truncate mt-0.5">{t.last_message_preview || "No messages yet"}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </Tile>
  );
}

// ---------------------------------------------------------------------------
// New Activity — unified feed across agent_outputs / decisions / drills
// ---------------------------------------------------------------------------
const ACTIVITY_KIND_META = {
  agent_output: { tone: "cyan",    icon: "Sparkles" },
  decision:     { tone: "rose",    icon: "GitBranch" },
  drill:        { tone: "emerald", icon: "Target" },
};

function ActivityTile({ items, nav }) {
  const list = (items || []).slice(0, 6);
  return (
    <Tile className="col-span-12 lg:col-span-6" delay={0.18} testId="family-activity-tile">
      <GlassCard className="p-6 h-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">Activity Feed</h3>
          </div>
          <StatusPill tone="cyan">Across all surfaces</StatusPill>
        </div>

        {list.length === 0 ? (
          <div className="mt-6 glass p-4 text-2 text-sm">
            No new advisor activity yet. We'll surface scouting updates, decisions, and prep drills here as they happen.
          </div>
        ) : (
          <ul className="mt-4 space-y-2">
            {list.map((a, i) => {
              const meta = ACTIVITY_KIND_META[a.kind] || ACTIVITY_KIND_META.agent_output;
              return (
                <li key={`${a.kind}-${a.id}`} data-testid={`family-activity-${i}`}>
                  <button
                    onClick={() => a.href && nav(a.href)}
                    className="w-full text-left glass p-3 flex items-start gap-3 hover:ring-1 hover:ring-cyan-300/30 transition"
                  >
                    <div className={`h-8 w-8 rounded-lg shrink-0 grid place-items-center ring-1 ${
                      meta.tone === "cyan" ? "bg-cyan-300/10 ring-cyan-300/25 text-cyan-200" :
                      meta.tone === "rose" ? "bg-rose-400/10 ring-rose-400/25 text-rose-200" :
                      "bg-emerald-300/10 ring-emerald-300/25 text-emerald-200"
                    }`}>
                      <Activity className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-1 text-sm font-medium truncate">{a.title || a.subtitle}</div>
                        {a.status && (
                          <StatusPill tone={
                            a.status === "complete" || a.status === "published" || a.status === "decided" ? "emerald" :
                            a.status === "approved" || a.status === "in_progress" ? "cyan" :
                            a.status === "leaning" ? "amber" : "neutral"
                          }>{a.status.replace(/_/g, " ")}</StatusPill>
                        )}
                      </div>
                      {a.subtitle && a.subtitle !== a.title && <div className="text-2 text-xs truncate mt-0.5 capitalize">{a.subtitle}</div>}
                      <div className="text-3 text-[11px] mt-1 font-mono">{fmtRelative(a.ts)}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </GlassCard>
    </Tile>
  );
}

// ---------------------------------------------------------------------------
// Quick links — strip of tiles
// ---------------------------------------------------------------------------
function QuickLinksStrip({ nav }) {
  const links = [
    { key: "profile", icon: UserIcon, label: "Player Profile", to: "/app/me/profile" },
    { key: "evaluation", icon: ClipboardList, label: "Evaluation", to: "/app/me/evaluation" },
    { key: "draft-plan", icon: CalendarClock, label: "Draft Plan", to: "/app/me/draft-plan" },
    { key: "team-fits", icon: Target, label: "Team Fits", to: "/app/me/team-fits" },
    { key: "reports", icon: FileText, label: "Reports", to: "/app/me/reports" },
    { key: "contracts", icon: FileSignature, label: "Contract", to: "/app/me/contracts" },
    { key: "documents", icon: FolderOpen, label: "Documents", to: "/app/me/documents" },
  ];
  return (
    <Tile className="col-span-12" delay={0.22} testId="family-quicklinks">
      <GlassCard className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-1 font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-300" /> Quick Links
          </h3>
          <span className="text-3 text-xs">Jump straight in</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <button
                key={l.key}
                data-testid={`family-quicklink-${l.key}`}
                onClick={() => nav(l.to)}
                className="glass glass-hover p-4 text-left group"
              >
                <Icon className="h-4 w-4 text-cyan-300 group-hover:scale-110 transition-transform" />
                <div className="mt-3 text-1 text-sm font-medium">{l.label}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-3 text-[11px] group-hover:text-cyan-300 transition-colors">
                  Open <ArrowUpRight className="h-3 w-3" />
                </div>
              </button>
            );
          })}
        </div>
      </GlassCard>
    </Tile>
  );
}

// EmptyState fallback re-export keeps lint happy if unused elsewhere
export { EmptyState };
